import React, { useState, useEffect, useRef, createContext, useContext } from 'react';

// Onboarding Context
const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

// Icons
const InfoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const QuestionIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

// Tooltip Component
export const Tooltip = ({ children, content, position = 'top', show = true, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (!show) return;
    timeoutRef.current = setTimeout(() => {
      setShouldShow(true);
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShouldShow(false);
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 transform -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 transform -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 transform -translate-y-1/2 ml-2';
      default:
        return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = () => {
    switch (position) {
      case 'top':
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800';
      case 'bottom':
        return 'bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800';
      case 'left':
        return 'left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-800';
      case 'right':
        return 'right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-800';
      default:
        return 'top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800';
    }
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {shouldShow && (
        <div className={`absolute z-50 ${getPositionClasses()}`}>
          <div className={`bg-gray-800 text-white text-sm rounded-lg px-3 py-2 max-w-xs whitespace-nowrap shadow-lg transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            {content}
          </div>
          <div className={`absolute w-0 h-0 ${getArrowClasses()}`}></div>
        </div>
      )}
    </div>
  );
};

// Info Icon Component
export const InfoButton = ({ content, title, position = 'top' }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        title="More information"
      >
        <InfoIcon />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className={`absolute z-50 ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} left-1/2 transform -translate-x-1/2`}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-strong border border-gray-200 dark:border-gray-700 p-4 max-w-sm">
              {title && (
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  {title}
                </h4>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {content}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Help Icon Component (similar to InfoButton but with question mark)
export const HelpButton = ({ content, title, position = 'top' }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        title="Get help"
      >
        <QuestionIcon />
      </button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className={`absolute z-50 ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} left-1/2 transform -translate-x-1/2`}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-strong border border-gray-200 dark:border-gray-700 p-4 max-w-sm">
              {title && (
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  {title}
                </h4>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {content}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Inline Help Text Component
export const InlineHelp = ({ children, className = '' }) => {
  return (
    <p className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${className}`}>
      {children}
    </p>
  );
};

// Simplified Spotlight Component
export const Spotlight = ({ targetId, isActive, padding = 8 }) => {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!isActive || !targetId) return;

    const updatePosition = () => {
      const element = document.getElementById(targetId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        setPosition({
          top: rect.top + scrollTop - padding,
          left: rect.left + scrollLeft - padding,
          width: rect.width + (padding * 2),
          height: rect.height + (padding * 2)
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isActive, targetId, padding]);

  if (!isActive || !position) return null;

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300" />
      
      {/* Spotlight hole */}
      <div 
        className="absolute bg-transparent border-2 border-celadon-400 rounded-lg transition-all duration-300"
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          height: position.height,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 20px rgba(34, 197, 94, 0.5)'
        }}
      />
    </div>
  );
};

// Simplified Tour Step Component
export const TourStep = ({ 
  isActive, 
  title, 
  content, 
  position = 'bottom',
  targetId,
  onNext,
  onPrev,
  onSkip,
  onComplete,
  currentStep,
  totalSteps,
  showSkip = true
}) => {
  const [stepPosition, setStepPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isActive || !targetId) return;

    const updatePosition = () => {
      const element = document.getElementById(targetId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        let top, left;
        const modalWidth = 400; // Approximate modal width
        const modalHeight = 300; // Approximate modal height
        const padding = 20;
        
        // Simple positioning logic with better bounds checking
        switch (position) {
          case 'top':
            top = rect.top + scrollTop - modalHeight - padding;
            left = rect.left + scrollLeft + (rect.width / 2);
            break;
          case 'bottom':
            top = rect.bottom + scrollTop + padding;
            left = rect.left + scrollLeft + (rect.width / 2);
            break;
          case 'left':
            top = rect.top + scrollTop + (rect.height / 2);
            left = rect.left + scrollLeft - modalWidth - padding;
            break;
          case 'right':
            top = rect.top + scrollTop + (rect.height / 2);
            left = rect.right + scrollLeft + padding;
            break;
          default:
            top = rect.bottom + scrollTop + padding;
            left = rect.left + scrollLeft + (rect.width / 2);
        }
        
        // Ensure the modal stays within viewport bounds
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Adjust horizontal position
        if (position === 'top' || position === 'bottom') {
          left = Math.max(padding, Math.min(left, viewportWidth - modalWidth - padding));
        }
        
        // Adjust vertical position
        if (position === 'left' || position === 'right') {
          top = Math.max(padding, Math.min(top, viewportHeight - modalHeight + scrollTop - padding));
        }
        
        // Ensure minimum distances from screen edges
        top = Math.max(padding, top);
        left = Math.max(padding, left);
        
        setStepPosition({ top, left });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isActive, targetId, position]);

  if (!isActive) return null;

  const getPositionClasses = () => {
    switch (position) {
      case 'top':
        return 'transform -translate-x-1/2';
      case 'bottom':
        return 'transform -translate-x-1/2';
      case 'left':
      case 'right':
        return 'transform -translate-y-1/2';
      default:
        return 'transform -translate-x-1/2';
    }
  };

  return (
    <div 
      className={`fixed z-[9999] w-full max-w-sm ${getPositionClasses()}`}
      style={{ 
        top: stepPosition.top, 
        left: stepPosition.left,
        animation: 'fadeIn 0.3s ease-out'
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 pr-4 flex-1">
            {title}
          </h3>
          {showSkip && (
            <button
              onClick={onSkip}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex-shrink-0"
              aria-label="Skip onboarding"
            >
              <XIcon />
            </button>
          )}
        </div>
        
        <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed">
          {content}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            {Array.from({ length: totalSteps }, (_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-celadon-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
          
          <div className="flex items-center space-x-2 flex-shrink-0">
            {currentStep > 0 && (
              <button
                onClick={onPrev}
                className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                aria-label="Previous step"
              >
                <ChevronLeftIcon />
                <span className="ml-1">Previous</span>
              </button>
            )}
            
            {currentStep < totalSteps - 1 ? (
              <button
                onClick={onNext}
                className="flex items-center px-4 py-2 bg-celadon-500 text-white rounded-lg hover:bg-celadon-600 transition-colors text-sm font-medium min-w-[80px] justify-center"
                aria-label="Next step"
              >
                <span className="mr-1">Next</span>
                <ChevronRightIcon />
              </button>
            ) : (
              <button
                onClick={onComplete}
                className="px-4 py-2 bg-celadon-500 text-white rounded-lg hover:bg-celadon-600 transition-colors text-sm font-medium min-w-[80px]"
                aria-label="Complete onboarding"
              >
                Complete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Onboarding Provider
export const OnboardingProvider = ({ children, user }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingSteps, setOnboardingSteps] = useState([]);

  // Check if user needs onboarding
  useEffect(() => {
    if (user) {
      const hasCompletedOnboarding = localStorage.getItem(`banyan_onboarding_completed_${user.id}`);
      const hasSeenWelcome = localStorage.getItem(`banyan_welcome_seen_${user.id}`);
      
      if (!hasCompletedOnboarding && !hasSeenWelcome) {
        // User is new and hasn't seen onboarding
        setIsOnboardingActive(false); // Will be activated by welcome modal
      }
    }
  }, [user]);

  const startOnboarding = (steps) => {
    setOnboardingSteps(steps);
    setCurrentStep(0);
    setIsOnboardingActive(true);
  };

  const nextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    if (user) {
      localStorage.setItem(`banyan_onboarding_completed_${user.id}`, 'true');
    }
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    if (user) {
      localStorage.setItem(`banyan_onboarding_completed_${user.id}`, 'true');
    }
  };

  const value = {
    isOnboardingActive,
    currentStep,
    onboardingSteps,
    startOnboarding,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      {isOnboardingActive && onboardingSteps.length > 0 && (
        <>
          <Spotlight 
            targetId={onboardingSteps[currentStep]?.targetId}
            isActive={true}
          />
          <TourStep
            isActive={true}
            title={onboardingSteps[currentStep]?.title}
            content={onboardingSteps[currentStep]?.content}
            position={onboardingSteps[currentStep]?.position}
            targetId={onboardingSteps[currentStep]?.targetId}
            onNext={nextStep}
            onPrev={prevStep}
            onSkip={skipOnboarding}
            onComplete={completeOnboarding}
            currentStep={currentStep}
            totalSteps={onboardingSteps.length}
          />
        </>
      )}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider; 